import {
  useQuoteStatusUtils,
  useRequestStatusUtils,
} from '@axo/shared/data-access/hooks';
import {
  ace_index,
  loan_application,
  loan_quote,
  permissions,
} from '@axo/shared/data-access/types';
import { useState } from 'react';
import { usePaginationSearch } from '../../hooks/queries/usePaginationSearch';
import { ITableConfig } from '../../models/tableConfig';
import { currencyFormatter } from '../../utils/formatCurrency';
import { formatDatetime } from '../../utils/formatDate';
import { AceCard } from '../AceCard';
import { AceTable } from '../AceTable';
import { AceTag } from '../AceTag';
import { IconMenu } from '../IconMenu';
import { SearchPaginate } from '../SearchPaginate';
import { StatusTag } from '../StatusTag';
import styles from './CustomerHistoryCard.module.css';
import { useCustomerHistory } from '../../hooks/queries/useCustomerHistory';
import { searchResultModelMapper } from './searchConfig.tsx';
import { ApplicationLink } from '../ApplicationLink';
import { LenderName } from '../SearchResults/LenderName';
import { useHasAnyOfRoles } from '@axo/shared/data-access/provider';
import { SearchResultModel } from './SearchResultModel.ts';

export interface ICustomerHistory {
  application: loan_application.LoanApplication;
}

type CustomerHistoryData = SearchResultModel & {
  AcceptedByLenderIDs: Set<string>;
  DisbursedByLenderIDs: Set<string>;
};

export function CustomerHistoryCard({ application }: ICustomerHistory) {
  return (
    <AceCard
      id={`CustomerHistory.${application.CustomerID}`}
      title="Customer history"
      noBorder
      isExpandedByDefault={false}
    >
      <CustomerHistoryContent application={application} />
    </AceCard>
  );
}

function CustomerHistoryContent({ application }: ICustomerHistory) {
  const [PageNumber, setPageNumber] = useState(1);

  const customerHistory = useCustomerHistory(
    {
      customerID: application.CustomerID,
      marketCountry: application.MarketCountry,
      PageNumber,
    },
    application.CustomerID.length > 0,
  );

  const paginationSearch = usePaginationSearch({
    query: customerHistory,
    PageNumber,
    setPageNumber,
  });
  const canViewOtherApps = useHasAnyOfRoles([
    permissions.RoleName.Manager,
    permissions.RoleName.Sales,
    permissions.RoleName.SecuredSales,
    permissions.RoleName.Telemarketing,
  ]);
  const currencyFormat = currencyFormatter(application?.Currency);
  const quoteStatusUtils = useQuoteStatusUtils();
  const requestStatusUtils = useRequestStatusUtils();

  const renderLenderNames = (lenderIDs: Set<string>): JSX.Element => {
    if (lenderIDs.size === 0) return <span>n.a.</span>;
    return (
      <>
        {Array.from(lenderIDs).map((lenderID) => (
          <LenderName
            key={lenderID}
            marketCountry={application.MarketCountry}
            lenderID={lenderID}
          />
        ))}
      </>
    );
  };

  const config: ITableConfig<CustomerHistoryData> = {
    columns: {
      ApplicantRole: {
        label: 'Role',
        render: (value) => value?.toLocaleString() ?? '',
        width: 'xsmall',
      },
      Product: {
        label: 'Product',
        render: (_, item) => item.Product?.Name ?? '',
        width: 'small',
      },
      CompletedAt: {
        label: 'Completed',
        render: (value) => formatDatetime(value?.toString()),
        width: 'medium',
      },
      AppliedAmount: {
        label: 'Applied Amount',
        render: (value) => currencyFormat(value?.toString()) ?? '',
        width: 'small',
      },
      Status: {
        label: 'Status',
        render: (_value, item) => (
          <div>
            {item.Status && (
              <StatusTag
                classes={{ tag: styles.aceBankStatusTag }}
                status={item.Status}
              >
                {item.Status}
              </StatusTag>
            )}
          </div>
        ),
      },
      ApprovedByLenderIDs: {
        label: 'Offers',
        render: (_value, item) =>
          renderLenderNames(item.ApprovedByLenderIDs ?? new Set<string>()),
      },
      AcceptedByLenderIDs: {
        label: 'Accepted',
        render: (_value, item) => renderLenderNames(item.AcceptedByLenderIDs),
      },
      DisbursedByLenderIDs: {
        label: 'Disbursed',
        render: (_value, item) => renderLenderNames(item.DisbursedByLenderIDs),
      },
    },
    extra: canViewOtherApps
      ? [
          {
            key: 'Actions',
            label: 'Actions',
            width: 'large',
            render: (item) => (
              <ApplicationLink
                humanReadableID={item.HumanReadableID}
                id={item.LoanApplicationID}
                marketCountry={application.MarketCountry}
              />
            ),
          },
        ]
      : [],
  };

  const mapper = searchResultModelMapper(quoteStatusUtils, requestStatusUtils);

  function quoteIs(group: loan_quote.QuoteStatusGroup) {
    return function (quote: ace_index.LoanQuote): boolean {
      const status = quote.Status?.Name;
      return quoteStatusUtils.isInQuoteStatusGroup(status, group);
    };
  }

  function lenderIDsByQuoteStatusGroup(
    data: ace_index.LoanApplication,
    statusGroup: loan_quote.QuoteStatusGroup,
  ) {
    if (!data.LoanQuotes) return new Set<string>();

    return new Set(
      data.LoanQuotes.filter(quoteIs(statusGroup)).map(
        (quote) => quote.LenderID,
      ),
    );
  }

  const data =
    customerHistory.data?.Items.map((data) => ({
      ...mapper(data),
      Actions: '',
      AcceptedByLenderIDs: lenderIDsByQuoteStatusGroup(
        data,
        loan_quote.QuoteStatusGroup.Accepted,
      ),
      DisbursedByLenderIDs: lenderIDsByQuoteStatusGroup(
        data,
        loan_quote.QuoteStatusGroup.Disbursing,
      ),
    })) ?? [];

  return (
    <>
      <IconMenu>
        {customerHistory.data && (
          <AceTag>{`${
            customerHistory.data.Parameters.TotalRows < 1001
              ? customerHistory.data.Parameters.TotalRows
              : '1000+'
          } results`}</AceTag>
        )}
      </IconMenu>

      <AceTable<CustomerHistoryData>
        config={config}
        loading={customerHistory.isLoading}
        data={data}
        uniqueRowIDKey="LoanApplicationID"
        maxRows={10}
      />

      <SearchPaginate
        config={{ query: customerHistory, ...paginationSearch }}
      />
    </>
  );
}
